/**
 * useRoomDetail — single-room detail bundle for the Room Detail page.
 *
 * Fetches in one parallel batch (independent reads, RLS-scoped by org):
 *   - the room row with the campus/building/floor/type hierarchy joined
 *   - the currently-active cleaning_task on the room, if any (status
 *     IN pending / in_progress / completed — the cleaner's lifecycle
 *     plus the completed→approval window; approved/rejected are terminal
 *     and excluded so a stale row never wins over a fresh one)
 *   - open maintenance issues (status IN open / assigned / in_progress)
 *   - the most recent N status-history rows with the changed-by name,
 *     newest first
 *
 * No mutations originate from this hook. Transitions still go through
 * the room-transition Edge Function (CLAUDE.md ERROR 5 / rule 4).
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'
import type { RoomStatus }                from './useRoomBoard'
import type { MaintenanceIssueRow }       from './useMaintenanceIssues'

export type ActiveTaskStatus = 'pending' | 'in_progress' | 'completed'

export interface RoomDetailRoom {
  id:             string
  room_number:    string
  current_status: RoomStatus
  bed_count:      number
  is_active:      boolean
  campus_id:      string | null
  building_id:    string
  floor_id:       string
  room_type_id:   string | null
  campus:    { id: string; name: string }                                       | null
  building:  { id: string; name: string }                                       | null
  floor:     { id: string; label: string }                                      | null
  room_type: { id: string; name: string; default_cleaning_minutes: number }     | null
}

export interface RoomDetailActiveTask {
  id:                  string
  status:              ActiveTaskStatus
  cycle_type:          string
  started_at:          string | null
  completed_at:        string | null
  assigned_to_user_id: string | null
  assignee:    { id: string; full_name: string | null } | null
}

export interface RoomDetailHistoryEntry {
  id:                 string
  previous_status:    RoomStatus | null
  new_status:         RoomStatus
  reason:             string | null
  created_at:         string
  changed_by_user_id: string | null
  changed_by:         { id: string; full_name: string | null } | null
}

export interface RoomDetailBundle {
  room:    RoomDetailRoom
  task:    RoomDetailActiveTask | null
  issues:  MaintenanceIssueRow[]
  history: RoomDetailHistoryEntry[]
}

const ROOMS_TABLE = 'rooms'
const ACTIVE_TASK_STATUSES: readonly ActiveTaskStatus[] = ['pending', 'in_progress', 'completed']
const OPEN_ISSUE_STATUSES = ['open', 'assigned', 'in_progress'] as const
const HISTORY_LIMIT = 20

export function useRoomDetail(roomId: string | undefined): UseQueryResult<RoomDetailBundle> {
  const supabase = useSupabase()

  return useQuery({
    queryKey: ['room-detail', roomId],
    enabled:  !!roomId,
    queryFn: async (): Promise<RoomDetailBundle> => {
      if (!roomId) throw new Error('useRoomDetail: roomId is required')

      const [roomRes, taskRes, issuesRes, historyRes] = await Promise.all([
        supabase
          .from(ROOMS_TABLE)
          .select(
            'id, room_number, current_status, bed_count, is_active,' +
            ' campus_id, building_id, floor_id, room_type_id,' +
            ' campus:campuses(id, name),' +
            ' building:buildings(id, name),' +
            ' floor:floors(id, label),' +
            ' room_type:room_types(id, name, default_cleaning_minutes)',
          )
          .eq('id', roomId)
          .single(),
        supabase
          .from('cleaning_tasks')
          .select(
            'id, status, cycle_type, started_at, completed_at, assigned_to_user_id,' +
            ' assignee:profiles!cleaning_tasks_assigned_to_user_id_fkey(id, full_name)',
          )
          .eq('room_id', roomId)
          .in('status', ACTIVE_TASK_STATUSES)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('maintenance_issues')
          .select(
            'id, room_id, title, description, severity, status, blocks_readiness,' +
            ' opened_at, resolved_at, resolution_notes, assigned_to_user_id, reported_by_user_id,' +
            ' ai_triage_status, ai_suggested_severity, ai_suggested_category_id,' +
            ' ai_suggested_is_blocking, ai_triage_reason, ai_triage_reviewed_at,' +
            ' ai_triage_reviewed_by_user_id,' +
            ' ai_suggested_category:maintenance_categories!ai_suggested_category_id(id, name),' +
            ' room:rooms(id, room_number, building:buildings(id, name))',
          )
          .eq('room_id', roomId)
          .in('status', OPEN_ISSUE_STATUSES)
          .order('opened_at', { ascending: true }),
        supabase
          .from('room_status_history')
          .select(
            'id, previous_status, new_status, reason, created_at, changed_by_user_id,' +
            ' changed_by:profiles!room_status_history_changed_by_user_id_fkey(id, full_name)',
          )
          .eq('room_id', roomId)
          .order('created_at', { ascending: false })
          .limit(HISTORY_LIMIT),
      ])

      if (roomRes.error)    throw roomRes.error
      if (taskRes.error)    throw taskRes.error
      if (issuesRes.error)  throw issuesRes.error
      if (historyRes.error) throw historyRes.error

      return {
        room:    roomRes.data    as unknown as RoomDetailRoom,
        task:   (taskRes.data    ?? null) as unknown as RoomDetailActiveTask | null,
        issues: (issuesRes.data  ?? []) as unknown as MaintenanceIssueRow[],
        history:(historyRes.data ?? []) as unknown as RoomDetailHistoryEntry[],
      }
    },
    refetchInterval: 60_000,
  })
}
